import React, { HTMLProps } from "react";
import {
  Button as BaseUIButton,
  ButtonOverrides,
  ButtonProps as BaseButtonProps,
  KIND,
  SHAPE,
  SIZE,
} from "baseui/button";
import { mergeOverrides } from "baseui";
import { Theme } from "../../../../styling/baseui_theme";
import { Overrides } from "baseui/overrides";
import styled from "styled-components";

// currently only implemented to work as Primary button types
export enum Color {
  default = "default",
  negative = "negative",
  warning = "warning",
  positive = "positive",
}

const iconOverrides = {
  StartEnhancer: {
    style: ({ $theme, $size }: { $theme: Theme; $size?: BaseButtonProps["size"] }) => ({
      height: getIconSize($theme, $size),
      width: getIconSize($theme, $size),
      display: "flex",
      alignItems: "center",
    }),
  },
  EndEnhancer: {
    style: ({ $theme, $size }: { $theme: Theme; $size?: BaseButtonProps["size"] }) => ({
      height: getIconSize($theme, $size),
      width: getIconSize($theme, $size),
      display: "flex",
      alignItems: "center",
    }),
  },
};

// follows pattern for how Base determines button text font size
// https://github.com/uber/baseweb/blob/master/src/button/styled-components.js#L216
export const getIconSize = ($theme: Theme, $size: BaseButtonProps["size"]) => {
  switch ($size) {
    case SIZE.mini:
      return $theme.typography.font150.fontSize;
    case SIZE.compact:
      return $theme.typography.font250.fontSize;
    case SIZE.large:
      return $theme.typography.font450.fontSize;
    default:
      return $theme.typography.font350.fontSize;
  }
};

const negativeColorOverride = {
  BaseButton: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.negative300,
      ":hover": {
        backgroundColor: $theme.colors.negative300,
      },
      ":active": {
        backgroundColor: $theme.colors.negative200,
      },
      ":disabled": {
        backgroundColor: $theme.colors.negative50,
      },
    }),
  },
};

const warningColorOverride = {
  BaseButton: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.warning200,
      color: $theme.colors.contentPrimary,
      ":hover": {
        backgroundColor: $theme.colors.warning200,
      },
      ":active": {
        backgroundColor: $theme.colors.warning,
      },
      ":disabled": {
        backgroundColor: $theme.colors.warning50,
      },
    }),
  },
};

const positiveColorOverride = {
  BaseButton: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.positive500,
      ":hover": {
        backgroundColor: $theme.colors.positive500,
      },
      ":active": {
        backgroundColor: $theme.colors.positive300,
      },
      ":disabled": {
        backgroundColor: $theme.colors.positive50,
      },
    }),
  },
};

const defaultShapeOverride = {
  BaseButton: {
    style: () => ({
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      borderBottomRightRadius: "5px",
      borderBottomLeftRadius: "5px",
    }),
  },
};

const stretchedStyleOverride = {
  BaseButton: {
    style: () => ({
      width: "100%",
    }),
  },
};

const secondaryAndTertiaryOverrides = (buttonType: BaseButtonProps["kind"]) => ({
  BaseButton: {
    style: ({ $theme }: { $theme: Theme }) => ({
      textDecoration: buttonType === KIND.tertiary ? "underline" : "none",
      ":disabled": {
        color: $theme.colors.mono400,
        backgroundColor: $theme.colors.mono100,
      },
      ":hover": {
        color: $theme.colors.contentPrimary,
      },
      ":active": {
        backgroundColor: $theme.colors.backgroundInverseSecondary,
        color: $theme.colors.contentInversePrimary,
      },
      ":hover:active": {
        backgroundColor: $theme.colors.backgroundInverseSecondary,
        color: $theme.colors.contentInversePrimary,
      },
    }),
  },
});

export interface ButtonProps extends BaseButtonProps {
  buttonType?: BaseButtonProps["kind"];
  buttonShape?: BaseButtonProps["shape"];
  buttonSize?: BaseButtonProps["size"];
  leftIcon?: BaseButtonProps["startEnhancer"];
  rightIcon?: BaseButtonProps["endEnhancer"];
  href?: string;
  target?: string;
  color?: Color;
  isStretched?: boolean;
  className?: string | undefined;
  styletronOverrides?: Overrides<ButtonOverrides>;
  ariaLabel?: string | undefined;
  dataAnalyticsId?: string | undefined;
  dataTest?: string | undefined;
  tabIndex?: HTMLProps<HTMLButtonElement>["tabIndex"];
}

export const Button = ({
  disabled = false,
  buttonType = KIND.primary,
  buttonShape = SHAPE.default,
  buttonSize = SIZE.default,
  leftIcon,
  rightIcon,
  href,
  target,
  color,
  isStretched = false,
  isLoading = false,
  className,
  styletronOverrides,
  ariaLabel,
  dataAnalyticsId,
  dataTest,
  ...props
}: ButtonProps) => {
  let styleOverride;
  // backwards compatible fix: we still expose 'kind' even though it's been named 'buttonType'
  // but all of our custom styling adjustments hinge on using 'buttonType'
  if (props.kind && props.kind !== KIND.primary) {
    buttonType = props.kind;
  }

  // it is possible to have ex. secondary button types
  // using lighter shades of the negative/warning/positive color palette
  // but only the primary button type implements these colors for now
  if (buttonType === KIND.primary) {
    if (color === Color.negative) {
      styleOverride = mergeOverrides(negativeColorOverride, styleOverride);
    } else if (color === Color.warning) {
      styleOverride = mergeOverrides(warningColorOverride, styleOverride);
    } else if (color === Color.positive) {
      styleOverride = mergeOverrides(positiveColorOverride, styleOverride);
    }
  } else if (buttonType === KIND.secondary || buttonType === KIND.tertiary) {
    styleOverride = mergeOverrides(secondaryAndTertiaryOverrides(buttonType), styleOverride);
  }

  if (buttonShape === SHAPE.default) {
    styleOverride = mergeOverrides(defaultShapeOverride, styleOverride);
  }

  if (isStretched) {
    styleOverride = mergeOverrides(stretchedStyleOverride, styleOverride);
  }

  if (leftIcon || rightIcon) {
    styleOverride = mergeOverrides(styleOverride, iconOverrides);
  }

  if (styletronOverrides) {
    styleOverride = mergeOverrides(
      styleOverride as Overrides<ButtonOverrides>,
      styletronOverrides as Overrides<ButtonOverrides>,
    );
  }

  return (
    <BaseUIButton
      aria-label={ariaLabel}
      data-analytics-id={dataAnalyticsId}
      data-test={dataTest}
      disabled={disabled}
      kind={buttonType}
      shape={buttonShape}
      size={buttonSize}
      startEnhancer={leftIcon}
      endEnhancer={rightIcon}
      isLoading={isLoading}
      $as={href?.length ? "a" : undefined}
      {...(href?.length ? { href } : {})}
      {...(target?.length ? { target } : {})}
      {...(className?.length ? { className } : {})}
      {...props}
      overrides={styleOverride}
    />
  );
};

export const StyledButtonAsLink = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.linkText};
  &:focus {
    outline-color: ${({ theme }) => theme.colors.accent};
  }
  &:hover {
    text-decoration: underline;
    background-color: transparent;
  }
`;
